import React, { useCallback, useEffect, useMemo } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import Page from '../index'
import AdsListActions from './AdsListActions'
import InfoBlock from '../../features/components/InfoBlock'
import AdsList from '../CampaignsSummary/AdsList'
import NewLineItemCreatedModal from './NewLineItemCreatedModal'
import SocialAuthChecker from '../../features/components/SocialAuthChecker'
import AdDuplicationModal from '../../features/components/Modals/AdDuplicationModal'
import AdCopyPreviewsButton from '../../features/components/Buttons/AdCopyPreviewsButton'
// import SummaryGraphs from '../../features/components/SummaryGraphs'

import usePlatformConnected from '../../features/hooks/usePlatformConnected'

import { replaceRouteParams } from '../../helpers/url'
import { ROUTE_PARAMS, ROUTES } from '../../constants/routes'
import { getSelectedPlatform } from '../../helpers/handlePlatformSelection'
import { getAdStructureType } from './helpers'

// import {
//   setLineItemsSummaryFilter,
//   setLineItemsSummaryDateRange,
//   setLineItemsSummarySort
//   // getLineItemsSummaryReports,
//   // clearLineItemsSummaryReports
// } from '../../modules/actions/lineItemsSummary'
import { clearAds } from '../../modules/actions/ads'
import { getLineItem, clearLineItem } from '../../modules/actions/lineItems'
import { clearCampaign, getCampaign } from '../../modules/actions/campaigns'

import { lineItemErrorSelector, lineItemSelector, lineItemWasLoadedSelector } from '../../modules/selectors/lineItems'
import { selectedAdAccountsSelector } from '../../modules/selectors/adAccounts'
import { campaignErrorSelector, campaignSelector, campaignWasLoadedSelector } from '../../modules/selectors/campaigns'

import { FACEBOOK_PLATFORM, PLATFORM_LABELS, TIKTOK_PLATFORM } from '../../constants/selectLists/platformList'

import useStyles from '../../styles/common/summaryPages'

const LineItemsSummaryPage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const { line_item_id: lineItemId, campaign_id: campaignId } = useParams()

  // const searchTerm = useSearch()
  const lineItem = useSelector(lineItemSelector)
  const campaignError = useSelector(campaignErrorSelector)
  const lineItemError = useSelector(lineItemErrorSelector)
  const lineItemWasLoaded = useSelector(lineItemWasLoadedSelector)
  const selectedPlatform = getSelectedPlatform()
  const { [selectedPlatform]: selectedAdAccount = {} } = useSelector(selectedAdAccountsSelector)
  // const dateRange = useSelector(lineItemsSummaryDateRangerSelector)
  // const filter = useSelector(lineItemsSummaryFilterSelector)
  // const sort = useSelector(lineItemsSummarySortSelector)
  const campaign = useSelector(campaignSelector)
  const campaignWasLoaded = useSelector(campaignWasLoadedSelector)

  const { objective: campaignObjective } = campaign
  const { id: selectedAdAccountId } = selectedAdAccount

  const { name: lineItemName } = lineItem

  const isPlatformConnected = usePlatformConnected(selectedPlatform)

  // const { date_preset, startDate, endDate } = dateRange
  //
  // const summaryReportTitle = `Ad ${selectedPlatform === FACEBOOK_PLATFORM ? 'Set' : 'Group'} Report and Insights`

  const breadcrumbs = useMemo(
    () => [
      {
        title: 'Partnerships',
        url: ROUTES.partnerships
      },
      {
        title: 'Campaign Hub',
        url: ROUTES.campaigns
      },
      {
        title: 'Campaign Summary',
        url: replaceRouteParams(ROUTES.campaignsSummary, {
          [ROUTE_PARAMS.campaignId]: campaignId
        })
      },
      {
        title: getAdStructureType(selectedPlatform)
      }
    ],
    [selectedPlatform, campaignId]
  )

  const renderErrorMessage = useCallback(() => {
    if (campaignError) {
      return (
        <InfoBlock title={t('Sorry, we can’t find that campaign')} centered greyDescription>
          <div>{t('You might not have access to it, or it may have been deleted')}</div>
        </InfoBlock>
      )
    } else {
      return (
        <InfoBlock title={`Sorry, we can’t find that ${getAdStructureType(selectedPlatform)}`} centered greyDescription>
          <div>You might not have access to it, or it may have been deleted</div>
        </InfoBlock>
      )
    }
  }, [campaignError, selectedPlatform, t])

  // todo commented till post MVP or another
  // const getReportsHandler = useCallback(() => {
  //   dispatch(
  //     getLineItemsSummaryReports({
  //       platform: selectedPlatform,
  //       external_id: selectedAdAccountId,
  //       line_item_id: lineItemId,
  //       date_from: startDate && format(startDate, DATES_FORMAT_BE),
  //       date_to: endDate && format(endDate, DATES_FORMAT_BE),
  //       date_preset
  //     })
  //   )
  // }, [selectedPlatform, selectedAdAccountId, lineItemId, startDate, endDate, date_preset, dispatch])

  useEffect(() => {
    if (isPlatformConnected && !campaignWasLoaded) {
      dispatch(getCampaign({ id: campaignId, account: selectedAdAccountId }, selectedPlatform))
    }
  }, [selectedPlatform, isPlatformConnected, campaignWasLoaded, campaignId, selectedAdAccountId, dispatch])

  useEffect(() => {
    // campaignObjective currently available for facebook and tiktok
    if (isPlatformConnected && !lineItemWasLoaded && (campaignObjective || selectedPlatform !== FACEBOOK_PLATFORM)) {
      dispatch(
        getLineItem(
          {
            id: lineItemId,
            ...(campaignObjective && { campaign_objective: campaignObjective }),
            account: selectedAdAccountId
          },
          selectedPlatform
        )
      )
    }
  }, [
    dispatch,
    isPlatformConnected,
    lineItemId,
    selectedAdAccountId,
    campaignObjective,
    lineItemWasLoaded,
    selectedPlatform
  ])

  useEffect(() => {
    return () => {
      dispatch(clearCampaign())
      dispatch(clearLineItem())
      dispatch(clearAds())
    }
  }, [dispatch])

  return (
    <Page helmetTitle="Campaigns" className={classes.page} breadcrumbs={breadcrumbs}>
      {campaignError || lineItemError ? (
        renderErrorMessage()
      ) : (
        <SocialAuthChecker
          platform={selectedPlatform}
          authFailureText={`Please authenticate with ${
            PLATFORM_LABELS[selectedPlatform]
          } to retrieve the ${getAdStructureType(selectedPlatform)} details.`}
        >
          <h2 className={classes.welcomeMessage}>
            {lineItemWasLoaded ? (
              `${getAdStructureType(selectedPlatform)} summary for ‘${lineItemName}’`
            ) : (
              <Skeleton width={280} />
            )}
          </h2>

          <AdsList
            RightPanel={
              <>
                <AdsListActions />
                {selectedPlatform !== TIKTOK_PLATFORM && <AdCopyPreviewsButton />}
              </>
            }
            isLineItemSummaryPage
          />

          {/*todo commented till post MVP or another decision*/}
          {/*<SummaryGraphs*/}
          {/*  pageTitle={summaryReportTitle}*/}
          {/*  getReports={getReportsHandler}*/}
          {/*  clearReportsAction={clearLineItemsSummaryReports}*/}
          {/*  reportsSelector={lineItemsSummaryReportsSelector}*/}
          {/*  reportsLoadingSelector={lineItemsSummaryReportsLoadingSelector}*/}
          {/*/>*/}
          <AdDuplicationModal />
          <NewLineItemCreatedModal />
        </SocialAuthChecker>
      )}
    </Page>
  )
}

export default LineItemsSummaryPage
